import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTranslations, type TranslationKeys } from '@/i18n';
import { 
  type Locale, 
  DEFAULT_LOCALE, 
  LOCALE_CONFIGS, 
  type LocaleConfig,
  getLocaleFromPath,
  localizedPath,
  SUPPORTED_LOCALES,
} from '@/i18n/types';

interface I18nContextType {
  locale: Locale;
  t: TranslationKeys;
  config: LocaleConfig;
  setLocale: (locale: Locale) => void;
  localizedPath: (path: string) => string;
  allLocales: typeof LOCALE_CONFIGS;
  supportedLocales: readonly Locale[];
}

const I18nContext = createContext<I18nContextType | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Detect locale from URL path
  const locale = useMemo<Locale>(() => {
    const detected = getLocaleFromPath(location.pathname);
    return detected || DEFAULT_LOCALE;
  }, [location.pathname]);

  const t = useMemo(() => getTranslations(locale), [locale]);
  const config = useMemo(() => LOCALE_CONFIGS[locale], [locale]);

  const setLocale = useCallback((newLocale: Locale) => {
    // Get the current path without locale prefix
    const currentPath = location.pathname.replace(/^\/(es|de|fr|it|pt|en)(\/|$)/, '/');
    const newPath = localizedPath(newLocale, currentPath || '/');
    
    // Save preference
    try {
      localStorage.setItem('punto-hispano-locale', newLocale);
    } catch {}
    
    navigate(newPath);
  }, [location.pathname, navigate]);

  const getLocalizedPath = useCallback((path: string) => {
    return localizedPath(locale, path);
  }, [locale]);

  const value = useMemo<I18nContextType>(() => ({
    locale,
    t,
    config,
    setLocale,
    localizedPath: getLocalizedPath,
    allLocales: LOCALE_CONFIGS,
    supportedLocales: SUPPORTED_LOCALES,
  }), [locale, t, config, setLocale, getLocalizedPath]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}

// Convenience hook for just translations
export function useTranslation() {
  const { t, locale, config } = useI18n();
  return { t, locale, config };
}
